import { useState, useEffect, useCallback } from 'react';
import { X, FileJson, AlertCircle } from 'lucide-react';
import type { SalesAnalysisReport } from '../types/report';

interface JsonInputModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: SalesAnalysisReport) => void;
}

export const JsonInputModal = ({ isOpen, onClose, onSubmit }: JsonInputModalProps) => {
  const [jsonText, setJsonText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleEscape = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleEscape]);

  if (!isOpen) return null;

  const extractJson = (text: string): string => {
    // \n 문자열을 실제 개행으로 변환
    const normalized = text.replace(/\\n/g, '\n');
    const firstBrace = normalized.indexOf('{');
    const lastBrace = normalized.lastIndexOf('}');
    if (firstBrace === -1 || lastBrace === -1 || firstBrace > lastBrace) {
      return '';
    }
    return normalized.substring(firstBrace, lastBrace + 1);
  };

  const handleSubmit = () => {
    setError(null);

    const cleanedJson = extractJson(jsonText);

    if (!cleanedJson.trim()) {
      setError('JSON 데이터를 입력해주세요.');
      return;
    }

    try {
      const parsed = JSON.parse(cleanedJson) as SalesAnalysisReport;

      // Basic validation
      if (!parsed.reportMetadata || !parsed.executiveSummary || !parsed.unitPricingAnalysis) {
        setError('필수 필드가 누락되었습니다. 올바른 형식의 JSON을 입력해주세요.');
        return;
      }

      onSubmit(parsed);
      setJsonText('');
      onClose();
    } catch {
      setError('유효하지 않은 JSON 형식입니다. JSON 문법을 확인해주세요.');
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={handleBackdropClick}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        className="w-full max-w-3xl rounded-lg bg-white shadow-xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div className="flex items-center gap-2">
            <FileJson className="h-5 w-5 text-blue-600" />
            <h2 id="modal-title" className="text-lg font-semibold">비교 데이터 입력</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="닫기"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          <p className="mb-4 text-sm text-gray-600">
            리포트와 동일한 형식의 JSON 데이터를 입력하세요. 입력한 데이터는 기존 리포트와 나란히 비교됩니다.
          </p>

          <textarea
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
            placeholder='{"reportMetadata": {...}, "executiveSummary": "...", ...}'
            className="h-80 w-full rounded-lg border border-gray-300 p-4 font-mono text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          />

          {error && (
            <div className="mt-4 flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-gray-700 hover:bg-gray-100"
          >
            취소
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
          >
            비교 시작
          </button>
        </div>
      </div>
    </div>
  );
};
